import React from 'react';
import { signS3Url } from '@/utils/sign-s3-url';
import PDFViewerFooter from './pdf-viewer-footer';
import PDFViewerHeader from './pdf-viewer-header';
import { extractLatestPdfUrl, getListingInfo } from './pdf-viewer-utils';
import * as viewer from './property-details-pdf-view-styles';
import Viewer from './viewer';

/**
 * Smart Component for rendering the PDF Viewer. Contains Header, Viewer, and Footer section
 * The Header Section: Contains Listing History title and two RSuite Cascading Selectors
 * The 1st Cascader is for selecting the listing ID, the second is for choosing scrape date
 *
 * The Viewer Section: Uses react-pdf to convert pdf to html canvas for nice displaying
 * The Viewer controls the size of the modal and also has the react-to-print functionality
 *
 * The Footer: Consists of two buttons 1) Print 2) Download
 * Print fires a loading animation and triggers react-to-print which consumes the viewer
 * Download using the an a href object with download attr.
 *
 * @export PropertyDetailsPDFViewer - Viewer component with Header, Viewer, Footer
 * @class PropertyDetailsPDFViewer
 * @extends {React.Component}
 */
class PropertyDetailsPDFViewer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      pdfPages: 0, // Number of pages the pdf contains
      printMode: false, // Whether or not printMode has been activated
      pdfFilePath: '', // The presigned url file path
      printLoading: false, // determiner for print button loader
      currentListingId: null, // The current listing being looked at
      currentPlatform: this.props.currentPlatform,
    };

    this.printContent = React.createRef(null); // will hold the content to print
  }

  /**
   * Gets the 1st pdf url for this listing, and makes a pre-signed url out of it
   * This presigned url is used to fetch the pdf from bucket with permission.
   *
   * NOTE: since this is called on construction, this.s3 can be used elsewhere
   * for additional fetches
   *
   * @memberof PropertyDetailsPDFViewer
   */
  async componentDidMount() {
    this.setState({ currentListingId: this.props.listingId }, async () => {
      const { currentPlatform, listings, listingId } = this.props;
      const listing = getListingInfo(listings, currentPlatform, listingId);
      const firstUrl = extractLatestPdfUrl(listing);
      const url = await signS3Url(firstUrl);
      this.setState({ pdfFilePath: url });
    });
  }

  /**
   * Handler function for switching to the correct pdf on listing selection
   * Note: when the use changes listings, the 1st url is chosen as priority
   *
   * @memberof PropertyDetailsPDFViewer
   */
  handleUserSelectedListing = async ([platform, listingId]) => {
    const listingInfo = getListingInfo(
      this.props.listings,
      platform,
      listingId
    );
    const firstPdfUrl = extractLatestPdfUrl(listingInfo);
    const url = await signS3Url(firstPdfUrl);

    /**
     * Setting pdfPages to 0 momentarily to protect against prototype warning from `Viewer`
     * pdfPages will be set to its correct value in `onDocumentLoadSuccess`
     */
    this.setState({
      pdfFilePath: url,
      pdfPages: 0,
      currentListingId: listingId,
      currentPlatform: platform,
    });
  };

  /**
   * Handler function for switching to the correct pdf on date selection
   *
   * @memberof PropertyDetailsPDFViewer
   */
  handleUserSelectedDate = async (listing, date) => {
    if (!date.children) {
      const { url: pdfUrl } = listing.pdfs.find((pdf) => {
        const pdfDate = pdf.scrapeDate;
        const selectedDate = date.value;
        return pdfDate === selectedDate;
      });

      const url = await signS3Url(pdfUrl);
      this.setState({ pdfFilePath: url });
    }
  };

  /**
   * When the document loads, react-pdf tells us the number of pages the pdf has.
   * Save it into state to be used to create ```page``` objects
   *
   * @memberof PropertyDetailsPDFViewer
   */
  onDocumentLoadSuccess = ({ numPages }) => {
    this.setState({ pdfPages: numPages });
  };

  /**
   * Render function for PDF Viewer
   *
   * @returns Header, Viewer, and Footer Components
   * @memberof PropertyDetailsPDFViewer
   */
  render() {
    return (
      <viewer.PDFViewerModal
        size="md"
        backdrop={true}
        onClose={this.props.closePdfModal}
        open={this.props.showPdfModal}
      >
        {this.state.currentListingId && (
          <PDFViewerHeader
            listings={this.props.listings}
            onListingSelect={this.handleUserSelectedListing}
            onDateSelect={this.handleUserSelectedDate}
            currentPlatform={this.state.currentPlatform}
            currentListingId={this.state.currentListingId}
            closePdfModal={this.props.closePdfModal}
          />
        )}

        <Viewer
          numPages={this.state.pdfPages}
          pdfFilePath={this.state.pdfFilePath}
          printContentRef={this.printContent}
          printMode={this.state.printMode}
          onDocumentLoadSuccess={this.onDocumentLoadSuccess}
        />

        <PDFViewerFooter
          loading={this.state.printLoading}
          onDownloadClick={this.onDownloadClick}
          pdfFilePath={this.state.pdfFilePath}
          /**
           * Access the "current" so that when the ref updates,
           *  this child component also receives the update
           */
          printContentRef={this.printContent?.current}
          setPrintModeBefore={() =>
            this.setState({ printMode: true, printLoading: true })
          }
          setPrintModeAfter={() => this.setState({ printMode: false })}
          setPrintLoading={() => this.setState({ printLoading: false })}
        />
      </viewer.PDFViewerModal>
    );
  }
}

export default PropertyDetailsPDFViewer;
